import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_TEST_PACKAGE,
  type TestPackage,
  type WeldPosition,
} from "@/lib/pipe-welds";
import { DEFAULT_TAKEOFF, type TakeoffSettings } from "@/lib/pipe-insulation";

const KEY = "tt.pipe-sketch.takeoff.v1";

interface Stored {
  weldPositions: Record<string, WeldPosition>;
  shopFabDefault: boolean;
  test: TestPackage;
  takeoff: TakeoffSettings;
}

const FALLBACK: Stored = {
  weldPositions: {},
  shopFabDefault: false,
  test: DEFAULT_TEST_PACKAGE,
  takeoff: DEFAULT_TAKEOFF,
};

function read(): Stored {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return FALLBACK;
    const p = JSON.parse(raw) as Partial<Stored>;
    return {
      weldPositions: p.weldPositions ?? {},
      shopFabDefault: !!p.shopFabDefault,
      test: { ...DEFAULT_TEST_PACKAGE, ...(p.test ?? {}) },
      takeoff: { ...DEFAULT_TAKEOFF, ...(p.takeoff ?? {}) },
    };
  } catch {
    return FALLBACK;
  }
}

/**
 * Weld positions, pressure-test parameters and insulation / coating settings.
 * Persisted on the device so a spool keeps its fab intent across sessions —
 * and works with no connection.
 */
export function useSketchTakeoff() {
  const [state, setState] = useState<Stored>(FALLBACK);

  useEffect(() => setState(read()), []);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      /* storage full — ignore */
    }
  }, [state]);

  const setWeldPosition = useCallback((id: string, position: WeldPosition) => {
    setState((s) => ({ ...s, weldPositions: { ...s.weldPositions, [id]: position } }));
  }, []);

  const setAllWelds = useCallback((ids: string[], position: WeldPosition) => {
    setState((s) => {
      const next = { ...s.weldPositions };
      for (const id of ids) next[id] = position;
      return { ...s, weldPositions: next };
    });
  }, []);

  const resetWelds = useCallback(() => {
    setState((s) => ({ ...s, weldPositions: {} }));
  }, []);

  const setShopFabDefault = useCallback((shopFabDefault: boolean) => {
    setState((s) => ({ ...s, shopFabDefault }));
  }, []);

  const setTest = useCallback((patch: Partial<TestPackage>) => {
    setState((s) => ({ ...s, test: { ...s.test, ...patch } }));
  }, []);

  const setTakeoff = useCallback((patch: Partial<TakeoffSettings>) => {
    setState((s) => ({ ...s, takeoff: { ...s.takeoff, ...patch } }));
  }, []);

  return {
    weldPositions: state.weldPositions,
    shopFabDefault: state.shopFabDefault,
    test: state.test,
    takeoff: state.takeoff,
    setWeldPosition,
    setAllWelds,
    resetWelds,
    setShopFabDefault,
    setTest,
    setTakeoff,
  };
}
